import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { toast } from 'sonner';
import { Eye, EyeOff, KeyRound, Check } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function SetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (password !== confirm) {
      toast.error('Les mots de passe ne correspondent pas.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/auth/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Lien invalide ou expiré');
      login(data.access_token, data.user);
      setDone(true);
      toast.success('Mot de passe défini ! Bienvenue.');
      setTimeout(() => {
        window.location.href = data.user?.role === 'admin' ? '/admin' : '/mon-compte';
      }, 1200);
    } catch (err) {
      toast.error(err.message || 'Erreur. Réessayez.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />
      <div className="flex items-center justify-center" style={{ minHeight: '80vh', padding: '2rem' }}>
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{ background: 'var(--teal)', boxShadow: '0 8px 24px rgba(93,188,191,0.25)' }}>
              <KeyRound size={22} color="#0B1E20" />
            </div>
            <h1 className="font-display text-2xl font-bold" style={{ color: 'var(--ink)' }}>
              Définir mon mot de passe
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
              Accédez à votre espace Show Événement
            </p>
          </div>

          <div className="p-6 rounded-2xl" style={{ background: '#FFFFFF', border: '1px solid var(--border)', boxShadow: '0 8px 32px rgba(26,26,46,0.06)' }}>
            {!token ? (
              <p className="text-sm text-center" data-testid="set-password-no-token" style={{ color: '#D94B5D' }}>
                Lien invalide. Merci de refaire une demande depuis la page de connexion.
              </p>
            ) : done ? (
              <div className="text-center py-4" data-testid="set-password-success">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ background: 'var(--teal-mist)' }}>
                  <Check size={28} style={{ color: 'var(--teal)' }} />
                </div>
                <p className="font-display font-bold text-lg" style={{ color: 'var(--ink)' }}>Compte activé</p>
                <p className="text-sm mt-2" style={{ color: 'var(--text-muted)' }}>Redirection en cours…</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>Nouveau mot de passe *</label>
                  <div className="relative">
                    <input type={showPwd ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                      autoComplete="new-password" data-testid="set-password-input"
                      className="w-full px-3 py-2.5 pr-10 rounded-xl text-sm"
                      style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                    <button type="button" onClick={() => setShowPwd(!showPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }}>
                      {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Au moins 8 caractères.</p>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>Confirmer le mot de passe *</label>
                  <input type={showPwd ? 'text' : 'password'} value={confirm} onChange={e => setConfirm(e.target.value)} required
                    autoComplete="new-password" data-testid="set-password-confirm-input"
                    className="w-full px-3 py-2.5 rounded-xl text-sm"
                    style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                </div>
                <button type="submit" disabled={loading} data-testid="set-password-submit-btn"
                  className="w-full btn-teal py-3 rounded-xl text-sm font-bold disabled:opacity-50 mt-2">
                  {loading ? '…' : 'Définir mon mot de passe'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
